import {
  getSectionToInsert,
  getSectionTypes
} from "@/lib/singularity/singularity";
import { globalStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import { SectionDescription } from "@/types/singularity";
import { getChatMessagesToRequestSectionPopulation } from "./sections";
import { copyNode, findNode, getBlankTree, moveNode } from "@/lib/tree/tree";
import { callAIBatch, BatchRequest } from "./api/api";
import { ChatMessage } from "@/lib/ai/types";
import { populateTreeFromBatchResponse, populateTreeWithImages } from "./pages/background-page-generation";
import { jsonParseOrUndefined } from "@/util/json-parse-or-undefined";
import { addAiJobAndGetValueReturnedByCallback } from "@/futurelayer/ai-job-manager/ai-jobs";

export async function generateSectionWithAI(
  description: string,
  generateBeforeInserting: boolean
): Promise<TreeNode | null> {
  const type = await determineSectionType(description);
  if (!type) {
    console.error("Failed to determine section type");
    return null;
  }

  const sectionDescription: SectionDescription = {
    type: type.type,
    sectionFriendlyName: type.friendlyName,
    description: description
  };

  const sectionTemplate = getSectionToInsert(sectionDescription);

  if (!sectionTemplate) {
    console.error("Failed to get section template for type:", type.type);
    return null;
  }

  if (!generateBeforeInserting) {
    return sectionTemplate;
  }

  const tempTree = getBlankTree();
  const sectionNode = copyNode(tempTree, sectionTemplate);
  moveNode(tempTree, sectionNode.id, tempTree.root.id, 0);

  const batchResponses = await addAiJobAndGetValueReturnedByCallback(
    async (abortController) => {
      return await callAIBatch([
        {
          id: String(sectionNode.id),
          messages: getChatMessagesToRequestSectionPopulation(sectionNode)
        }
      ], abortController);
    },
    {
      jobType: 'section_rewrite',
      documentId: 0,
      elementId: sectionNode.id,
      description: `Generating section: ${description}`,
      priority: 'critical',
      snapshotLabel: 'Create Section With AI'
    }
  );

  const populatedTree = populateTreeFromBatchResponse(tempTree, batchResponses);
  const treeWithImages = await addAiJobAndGetValueReturnedByCallback(
    async (abortController) => {
      return await populateTreeWithImages(populatedTree, abortController);
    },
    {
      jobType: 'section_image_replacement',
      documentId: 0,
      elementId: sectionNode.id,
      description: `Populating images for section: ${description}`,
      priority: 'high',
      snapshotLabel: 'Populate Images With AI'
    }
  );

  const completedNode = findNode(treeWithImages, sectionNode.id);

  if (!completedNode) return null;

  return completedNode;
}

async function determineSectionType(
  description: string
): Promise<{type: string, friendlyName: string} | undefined> {
  const websiteName = globalStore.aiSettings.prompt.businessName;
  const websiteDescription = globalStore.aiSettings.prompt.businessDescription;

  const sectionTypes = getSectionTypes();
  const sectionTypesString = sectionTypes.map(type => `'${type}'`).join(", ");

  const messages: ChatMessage[] = [
    {
      role: "system",
      content: `You are an assistant that determines the appropriate section type for a website based on a description.

Website name: ${websiteName}
Website description: ${websiteDescription}

Valid section types are: ${sectionTypesString}

You must respond with a JSON object containing a "type" field with one of the valid section types, and a "friendlyName" field which is a short friendly name (1 to 3 words) that describes the content of the section.`
    },
    {
      role: "user",
      content: `Based on the following description, determine which section type to use:

"${description}"

Respond with JSON format: {"type": "chosen_section_type","friendlyName": "Friendly Name For The Section"}`
    }
  ];

  const batchRequest: BatchRequest = {
    id: "section-type-determination",
    messages: messages
  };

  try {
    const responses = await addAiJobAndGetValueReturnedByCallback(
      async (abortController) => {
        return await callAIBatch([batchRequest], abortController);
      },
      {
        jobType: 'section_rewrite',
        documentId: 0,
        elementId: 'section-type-determination',
        description: `Determining section type for: ${description}`,
        priority: 'critical',
        dontCreateSnapshot: true
      }
    );
    if (responses.length > 0 && responses[0].content) {
      const parsed = jsonParseOrUndefined(responses[0].content) as any;
      if (!parsed) return undefined;
      return {
        type: parsed.type,
        friendlyName: parsed.friendlyName
      };
    }
  } catch (error) {
    console.error("Failed to determine section type:", error);
  }

  return undefined;
}
